// Default margin tiers page (percentages)
// Updated: margins are expressed as percentages applied to (purchase price + logistics per plant).
// Defaults: 30, 25, 20, 15 for the fixed ranges 25-50, 51-75, 76-100, 101+.

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { Layers, Save, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';

interface MarginTier {
  minimum_quantity: number;
  maximum_quantity: number | null;
  margin_percentage: number;
}

const FIXED_RANGES: { min: number; max: number | null; label: string }[] = [
  { min: 25, max: 50, label: '25 - 50 stuks' },
  { min: 51, max: 75, label: '51 - 75 stuks' },
  { min: 76, max: 100, label: '76 - 100 stuks' },
  { min: 101, max: null, label: '101+ stuks' },
];

const DEFAULT_PERCENTAGES = [30, 25, 20, 15];

export default function AdminDefaultMargins() {
  const [percentages, setPercentages] = useState<number[]>(DEFAULT_PERCENTAGES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadMargins();
  }, []);

  async function loadMargins() {
    try {
      setIsLoading(true);
      const { data } = await supabase
        .from('delivery_settings')
        .select('setting_value')
        .eq('setting_key', 'default_margin_tiers')
        .maybeSingle();

      if (data?.setting_value) {
        const tiers = data.setting_value as MarginTier[];
        const sorted = [...tiers].sort((a, b) => a.minimum_quantity - b.minimum_quantity);
        setPercentages(sorted.map(t => Number(t.margin_percentage) || 0));
      }
    } catch {
      setMessage({ type: 'error', text: 'Fout bij het laden van margetabellen' });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave() {
    setIsSaving(true);
    setMessage(null);

    try {
      const tiers: MarginTier[] = FIXED_RANGES.map((range, i) => ({
        minimum_quantity: range.min,
        maximum_quantity: range.max,
        margin_percentage: percentages[i] ?? 0,
      }));

      const { error } = await supabase
        .from('delivery_settings')
        .update({ setting_value: tiers })
        .eq('setting_key', 'default_margin_tiers');

      if (error) throw error;
      setMessage({ type: 'success', text: 'Standaard margetabellen opgeslagen' });
    } catch {
      setMessage({ type: 'error', text: 'Fout bij het opslaan' });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#5a6552]" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 space-y-6">
      <Breadcrumb pageName="Standaard margetabellen" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Standaard margetabellen</h1>
          <p className="text-[#5a6552] mt-1">
            Deze marges (in procenten) worden gebruikt wanneer een variant geen eigen marges heeft ingesteld
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Opslaan
        </Button>
      </div>

      {message && (
        <Alert
          variant={message.type === 'error' ? 'destructive' : 'default'}
          className={message.type === 'success' ? 'border-[#1e2b22]/30 bg-[#e7e1d6]/40' : ''}
        >
          {message.type === 'success' ? <CheckCircle2 className="h-4 w-4 text-[#1e2b22]" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription className={message.type === 'success' ? 'text-[#1e2b22]' : ''}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <Card className="border border-[#e7e1d6] bg-white">
        <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
          <CardTitle className="flex items-center gap-2 text-[#1e2b22]">
            <Layers className="w-5 h-5" />
            Marge per staffel
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow className="border-[#e7e1d6]">
                <TableHead className="text-[#5a6552] uppercase text-xs tracking-wider">Hoeveelheid</TableHead>
                <TableHead className="text-[#5a6552] uppercase text-xs tracking-wider">Marge (%)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {FIXED_RANGES.map((range, index) => (
                <TableRow key={index} className="border-[#e7e1d6]">
                  <TableCell className="font-medium text-[#1e2b22]">{range.label}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        className="w-28 h-9 border-[#e7e1d6] focus-visible:ring-[#1e2b22]"
                        value={percentages[index] ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const parsed = value === '' ? 0 : parseFloat(value);
                          const next = [...percentages];
                          next[index] = isNaN(parsed) ? 0 : parsed;
                          setPercentages(next);
                        }}
                      />
                      <span className="text-[#5a6552]">%</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-6 p-4 bg-[#fcfaf6] rounded-lg border border-[#e7e1d6]">
            <p className="text-sm text-[#5a6552]">
              <strong className="text-[#1e2b22]">Let op:</strong> Deze percentages worden toegepast op de inkoopprijs plus de logistieke kosten per plant.
              Ze worden alleen gebruikt als een variant geen eigen marge-instellingen heeft.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
